import React, { useState } from 'react';
import { getApiClient } from '../../api/apiClient';
import { toast } from 'react-toastify';
import { 
  XMarkIcon,
  UserPlusIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  WrenchScrewdriverIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const AddEmployeeModal = ({ isOpen, onClose, onSuccess, shop }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    shopId: '',
    name: '',
    email: '',
    phone: '',
    specialization: 'MECHANICAL',
    workingHours: []
  });

  const specializations = [
    { value: 'MECHANICAL', label: 'Mechanic' },
    { value: 'ITP', label: 'ITP' },
  ];

  const daysOfWeek = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' }
  ];

  // Initialize form data when modal opens
  React.useEffect(() => {
    if (isOpen && shop) {
      setFormData({
        shopId: shop.id,
        name: '',
        email: '',
        phone: '',
        specialization: 'MECHANICAL',
        workingHours: []
      });
    }
  }, [isOpen, shop]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addWorkingHour = () => {
    setFormData(prev => ({
      ...prev,
      workingHours: [
        ...prev.workingHours,
        { dayOfWeek: 1, startTime: '09:00', endTime: '18:00' }
      ]
    }));
  };

  const removeWorkingHour = (index) => {
    setFormData(prev => ({
      ...prev,
      workingHours: prev.workingHours.filter((_, i) => i !== index)
    }));
  };

  const updateWorkingHour = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      workingHours: prev.workingHours.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const validateForm = () => {
    if (!formData.name || !formData.name.trim()) {
      toast.error('Please enter employee name');
      return false;
    }

    if (!formData.email || !formData.email.trim()) {
      toast.error('Please enter employee email');
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    if (!formData.phone || !formData.phone.trim()) {
      toast.error('Please enter employee phone number');
      return false;
    }

    // Validate working hours
    for (let i = 0; i < formData.workingHours.length; i++) {
      const wh = formData.workingHours[i];
      const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      
      if (!timeRegex.test(wh.startTime) || !timeRegex.test(wh.endTime)) {
        toast.error(`Invalid time format in working hours row ${i + 1}`);
        return false;
      }
      
      if (wh.startTime >= wh.endTime) {
        toast.error(`Start time must be before end time in working hours row ${i + 1}`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const client = getApiClient();
      await client.post('/admin/shop/add-employee', formData);

      toast.success('Employee added successfully!');
      onSuccess();
      
    } catch (error) {
      console.error('Error adding employee:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add employee';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-5 mx-auto p-6 border w-11/12 md:w-4/5 lg:w-1/2 max-w-2xl shadow-lg rounded-md bg-white max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <UserPlusIcon className="h-6 w-6 text-purple-600 mr-2" />
            <h3 className="text-xl font-semibold text-gray-900">
              Add New Employee
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Shop Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Shop Information</h4>
          <div className="text-sm text-blue-700">
            <p><strong>Brand Name:</strong> {shop?.name}</p>
            <p><strong>Shop ID:</strong> {shop?.id}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Employee Basic Information */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-800 border-b pb-2">
              Employee Information
            </h4>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  placeholder="John Doe"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  placeholder="john@autoservice.ro"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <div className="relative">
                <PhoneIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  placeholder="+40721234567"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Specialization */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specialization *
              </label>
              <div className="relative">
                <WrenchScrewdriverIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <select
                  value={formData.specialization}
                  onChange={(e) => handleInputChange('specialization', e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  disabled={loading}
                >
                  {specializations.map((spec) => (
                    <option key={spec.value} value={spec.value}>
                      {spec.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Working Hours */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-gray-800">
                Working Hours
              </h4>
              <button
                type="button"
                onClick={addWorkingHour}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                disabled={loading}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Schedule
              </button>
            </div>

            <div className="space-y-3">
              {formData.workingHours.map((workingHour, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md">
                  <select
                    value={workingHour.dayOfWeek}
                    onChange={(e) => updateWorkingHour(index, 'dayOfWeek', parseInt(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    disabled={loading}
                  >
                    {daysOfWeek.map((day) => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </select>

                  <input
                    type="time"
                    value={workingHour.startTime}
                    onChange={(e) => updateWorkingHour(index, 'startTime', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    disabled={loading}
                  />

                  <span className="text-gray-500">to</span>

                  <input
                    type="time"
                    value={workingHour.endTime}
                    onChange={(e) => updateWorkingHour(index, 'endTime', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    disabled={loading}
                  />

                  <button
                    type="button"
                    onClick={() => removeWorkingHour(index)}
                    className="inline-flex items-center p-2 border border-transparent rounded-md text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    disabled={loading}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {formData.workingHours.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  No working hours set. Click "Add Schedule" to add employee's schedule.
                </div>
              )}
            </div>
          </div>

          {/* Info box */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ClockIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Employee Information
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    This will add a new employee to the shop with:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Personal contact information</li>
                    <li>Job specialization and role</li>
                    <li>Working schedule (optional)</li>
                    <li>Integration with shop's appointment system</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Adding Employee...
                </div>
              ) : (
                'Add Employee'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEmployeeModal;